(function () {
  const SELECTORS = {
    section: '[data-hc-feedback-section]',
    form: '[data-hc-feedback-form]',
    request: '[data-hc-feedback-request]',
    additional: '[data-hc-feedback-additional]',
    appVersionField: '[data-hc-feedback-app-version-field]',
    codeField: '[data-hc-feedback-code-field]',
    appVersion: '[data-hc-feedback-app-version]',
    code: '[data-hc-feedback-code]',
    platformChip: '[data-platform]',
    platformInput: '[data-hc-feedback-platform-input]',
    profileId: '[data-hc-feedback-profile-id]',
    unlockId: '[data-hc-feedback-unlock-id]',
    deviceId: '[data-hc-feedback-device-id]',
    submit: '[data-hc-feedback-submit]',
    submitText: '[data-hc-feedback-submit-text]',
    loadingText: '[data-hc-feedback-loading-text]',
    status: '[data-hc-feedback-status]',
    config: '[data-hc-feedback-config]',
    error: '[data-error-for]',
  };

  const FEEDBACK_SUBMIT_MUTATION = `
    mutation FeedbackSubmit($input: CreateFeedbackInput!) {
      feedbackSubmit(input: $input)
    }
  `;

  function getQueryParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function getConfig(section) {
    const configEl = section.querySelector(SELECTORS.config);

    const fallback = {
      successText: 'Your request was sent successfully.',
      errorText: 'Something went wrong. Please try again.',
      tooManyRequestsText: 'Too many requests. Please try again later.',
      requiredText: 'This field is required.',
      emailInvalidText: 'Please enter a valid email.',
      platformRequiredText: 'Please select your platform.',
      codeInvalidText: 'Use letters and numbers only.',
    };

    if (!configEl) return fallback;

    try {
      return {
        ...fallback,
        ...JSON.parse(configEl.textContent || '{}'),
      };
    } catch {
      return fallback;
    }
  }

  function setLoading(section, loading) {
    const submit = section.querySelector(SELECTORS.submit);
    const submitText = section.querySelector(SELECTORS.submitText);
    const loadingText = section.querySelector(SELECTORS.loadingText);

    if (!submit) return;

    submit.disabled = loading;
    submit.classList.toggle('is-loading', loading);

    if (submitText) submitText.hidden = loading;
    if (loadingText) loadingText.hidden = !loading;
  }

  function showStatus(section, message, type) {
    const status = section.querySelector(SELECTORS.status);
    if (!status) return;

    status.hidden = false;
    status.textContent = message;
    status.dataset.type = type || 'neutral';
  }

  function hideStatus(section) {
    const status = section.querySelector(SELECTORS.status);
    if (!status) return;

    status.hidden = true;
    status.textContent = '';
    delete status.dataset.type;
  }

  function setFieldError(section, fieldName, message) {
    const errorEl = section.querySelector(`[data-error-for="${fieldName}"]`);
    const field = section.querySelector(`[name="${fieldName}"]`);

    if (errorEl) {
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    if (field) {
      field.classList.toggle('has-error', Boolean(message));
    }
  }

  function clearErrors(section) {
    section.querySelectorAll(SELECTORS.error).forEach((el) => {
      el.textContent = '';
      el.hidden = true;
    });

    section.querySelectorAll('.has-error').forEach((el) => {
      el.classList.remove('has-error');
    });
  }

  function syncQueryHiddenFields(section) {
    const profileIdInput = section.querySelector(SELECTORS.profileId);
    const unlockIdInput = section.querySelector(SELECTORS.unlockId);
    const deviceIdInput = section.querySelector(SELECTORS.deviceId);

    if (profileIdInput) profileIdInput.value = getQueryParam('pId') || '';
    if (unlockIdInput) unlockIdInput.value = getQueryParam('uId') || '';
    if (deviceIdInput) deviceIdInput.value = getQueryParam('dId') || '';
  }

  function updateConditionalFields(section) {
    const requestSelect = section.querySelector(SELECTORS.request);
    const additional = section.querySelector(SELECTORS.additional);
    const appVersionField = section.querySelector(SELECTORS.appVersionField);
    const codeField = section.querySelector(SELECTORS.codeField);
    const appVersionInput = section.querySelector(SELECTORS.appVersion);
    const codeInput = section.querySelector(SELECTORS.code);

    if (!requestSelect || !additional) return;

    const requestType = requestSelect.value;
    const isIssueWithApp = requestType === 'issueWithApp';
    const isIssueWithCode = requestType === 'issueWithCode';
    const shouldShowAdditional = isIssueWithApp || isIssueWithCode;

    additional.hidden = !shouldShowAdditional;

    if (appVersionField) appVersionField.hidden = !isIssueWithApp;
    if (codeField) codeField.hidden = !isIssueWithCode;

    if (appVersionInput) {
      appVersionInput.required = isIssueWithApp;
      if (!isIssueWithApp) appVersionInput.value = '';
    }

    if (codeInput) {
      codeInput.required = isIssueWithCode;
      if (!isIssueWithCode) codeInput.value = '';
    }

    if (!shouldShowAdditional) {
      section.querySelectorAll(SELECTORS.platformChip).forEach((chip) => {
        chip.classList.remove('is-active');
        chip.setAttribute('aria-pressed', 'false');
      });

      const platformInput = section.querySelector(SELECTORS.platformInput);
      if (platformInput) platformInput.value = '';

      setFieldError(section, 'platform', '');
      setFieldError(section, 'appVersion', '');
      setFieldError(section, 'code', '');
    }
  }

  function updatePlatformInput(section) {
    const activePlatforms = Array.from(section.querySelectorAll(`${SELECTORS.platformChip}.is-active`))
      .map((chip) => chip.dataset.platform)
      .filter(Boolean);

    const platformInput = section.querySelector(SELECTORS.platformInput);
    if (platformInput) platformInput.value = activePlatforms.join(',');
  }

  function getPlatformValue(section) {
    const platformInput = section.querySelector(SELECTORS.platformInput);
    if (!platformInput || !platformInput.value) return undefined;

    return platformInput.value
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function getFormValue(formData, key) {
    return String(formData.get(key) || '').trim();
  }

  function validate(section, input) {
    const config = getConfig(section);
    let valid = true;

    clearErrors(section);

    if (!input.name) {
      setFieldError(section, 'name', config.requiredText);
      valid = false;
    }

    if (!input.email) {
      setFieldError(section, 'email', config.requiredText);
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
      setFieldError(section, 'email', config.emailInvalidText);
      valid = false;
    }

    if (!input.requestType) {
      setFieldError(section, 'requestType', config.requiredText);
      valid = false;
    }

    if (!input.description) {
      setFieldError(section, 'description', config.requiredText);
      valid = false;
    }

    if (input.description && input.description.length > 500) {
      setFieldError(section, 'description', 'Maximum 500 characters.');
      valid = false;
    }

    if (input.name && input.name.length > 100) {
      setFieldError(section, 'name', 'Maximum 100 characters.');
      valid = false;
    }

    if (input.requestType === 'issueWithApp') {
      if (!input.appVersion) {
        setFieldError(section, 'appVersion', config.requiredText);
        valid = false;
      }

      if (!input.platform || !input.platform.length) {
        setFieldError(section, 'platform', config.platformRequiredText);
        valid = false;
      }
    }

    if (input.requestType === 'issueWithCode') {
      if (!input.code) {
        setFieldError(section, 'code', config.requiredText);
        valid = false;
      } else if (!/^[A-Za-z0-9]+$/.test(input.code)) {
        setFieldError(section, 'code', config.codeInvalidText);
        valid = false;
      }

      if (!input.platform || !input.platform.length) {
        setFieldError(section, 'platform', config.platformRequiredText);
        valid = false;
      }
    }

    return valid;
  }

  function buildFeedbackInput(section) {
    const form = section.querySelector(SELECTORS.form);
    const formData = new FormData(form);

    const requestType = getFormValue(formData, 'requestType');
    const appVersion = getFormValue(formData, 'appVersion');
    const code = getFormValue(formData, 'code');
    const platform = getPlatformValue(section);

    const input = {
      email: getFormValue(formData, 'email'),
      name: getFormValue(formData, 'name'),
      requestType,
      description: getFormValue(formData, 'description'),
      region: 'US',
      profileId: getQueryParam('pId') || undefined,
      unlockId: getQueryParam('uId') || undefined,
      deviceId: getQueryParam('dId') || undefined,
    };

    if (requestType === 'issueWithApp' && appVersion) {
      input.appVersion = appVersion;
    }

    if (requestType === 'issueWithCode' && appVersion) {
      input.appVersion = appVersion;
    }

    if (requestType === 'issueWithCode' && code) {
      input.code = code;
    }

    if ((requestType === 'issueWithApp' || requestType === 'issueWithCode') && platform && platform.length) {
      input.platform = platform;
    }

    Object.keys(input).forEach((key) => {
      if (input[key] === undefined || input[key] === '') {
        delete input[key];
      }
    });

    return input;
  }

  async function sendFeedbackToServer(endpoint, input) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
      body: JSON.stringify({
        query: FEEDBACK_SUBMIT_MUTATION,
        variables: {
          input,
        },
      }),
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      const error = new Error('Feedback request failed');
      error.status = response.status;
      error.result = result;
      throw error;
    }

    if (result && Array.isArray(result.errors) && result.errors.length) {
      const error = new Error('Feedback GraphQL error');
      error.result = result;
      throw error;
    }

    return result;
  }

  function isTooManyRequestsError(error) {
    if (error && error.status === 429) return true;

    const firstGraphQLError = error?.result?.errors?.[0];
    const code = firstGraphQLError?.extensions?.code;

    return code === 'TOO_MANY_REQUESTS';
  }

  function resetForm(section) {
    const form = section.querySelector(SELECTORS.form);
    if (!form) return;

    form.reset();

    section.querySelectorAll(SELECTORS.platformChip).forEach((chip) => {
      chip.classList.remove('is-active');
      chip.setAttribute('aria-pressed', 'false');
    });

    const platformInput = section.querySelector(SELECTORS.platformInput);
    if (platformInput) platformInput.value = '';

    updateConditionalFields(section);
    syncQueryHiddenFields(section);
  }

  function initSection(section) {
    if (section.dataset.hcFeedbackInitialized === 'true') return;

    const form = section.querySelector(SELECTORS.form);
    const requestSelect = section.querySelector(SELECTORS.request);
    const config = getConfig(section);

    if (!form) return;

    syncQueryHiddenFields(section);
    updateConditionalFields(section);
    clearErrors(section);

    if (requestSelect) {
      requestSelect.addEventListener('change', function () {
        updateConditionalFields(section);
      });
    }

    section.querySelectorAll(SELECTORS.platformChip).forEach((chip) => {
      chip.addEventListener('click', function () {
        const isActive = chip.classList.toggle('is-active');
        chip.setAttribute('aria-pressed', String(isActive));
        updatePlatformInput(section);
        setFieldError(section, 'platform', '');
      });
    });

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      hideStatus(section);

      const endpoint = section.dataset.feedbackEndpoint;
      const input = buildFeedbackInput(section);

      if (!validate(section, input)) {
        return;
      }

      if (!endpoint) {
        showStatus(section, config.errorText, 'error');
        return;
      }

      try {
        setLoading(section, true);

        await sendFeedbackToServer(endpoint, input);

        showStatus(section, config.successText, 'success');
        resetForm(section);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'contact_form_submit',
        });
      } catch (error) {
        if (isTooManyRequestsError(error)) {
          showStatus(section, config.tooManyRequestsText, 'neutral');
        } else {
          showStatus(section, config.errorText, 'error');
        }
      } finally {
        setLoading(section, false);
      }
    });

    section.dataset.hcFeedbackInitialized = 'true';
  }

  function init() {
    document.querySelectorAll(SELECTORS.section).forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
